import React, { useState } from 'react';
import { Smartphone, ChevronDown, ArrowRight, ArrowLeft, MessageCircle, Phone, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useTheme } from '../contexts/ThemeContext';
interface PhoneEntryScreenProps {
  onBack: () => void;
  onContinue: (phone: string, deliveryMethod: 'whatsapp' | 'call') => void;
}
export function PhoneEntryScreen({
  onBack,
  onContinue
}: PhoneEntryScreenProps) {
  const {
    isLight
  } = useTheme();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState<'whatsapp' | 'call'>('whatsapp');
  const [isLoading, setIsLoading] = useState(false);
  const handleContinue = () => {
    if (!phoneNumber) return;
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      onContinue(phoneNumber, deliveryMethod);
    }, 1000);
  };
  return <div className="h-full flex flex-col p-6 relative overflow-hidden" data-id="element-2698" data-mp="3ufoizczf2ew5">
      {/* Background orbs */}
      <div className={`absolute top-20 -right-16 w-48 h-48 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-vexu/15' : 'bg-vexu/10'}`} data-id="element-2699" data-mp="2ack0l2xktji" />

      {/* Header */}
      <div className="flex items-center mb-8 relative z-10" data-id="element-2700" data-mp="c91vnw48hhrb">
        <button onClick={onBack} className={`p-2 -ml-2 rounded-full border transition-colors ${isLight ? 'text-slate-500 hover:text-slate-900 bg-white/60 backdrop-blur-md border-slate-200' : 'text-zinc-400 hover:text-white bg-white/5 backdrop-blur-md border-white/10'}`} data-id="element-2701" data-mp="c77hd0ngwnw7">
          <ArrowLeft className="w-5 h-5" data-id="element-2702" data-mp="3kn0oiovwsn0l" />
        </button>
      </div>

      <div className="flex-1 flex flex-col pt-8 relative z-10" data-id="element-2703" data-mp="3pglnok98nn1d">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} data-id="element-2704" data-mp="2s0y0t64fdrez">
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-8 ${isLight ? 'bg-slate-900 text-white shadow-lg shadow-slate-900/20' : 'bg-white text-black shadow-lg shadow-white/10'}`} data-id="element-2705" data-mp="2kx7zoyfskirj">
            <Smartphone className="w-7 h-7" data-id="element-2706" data-mp="2tp9y6r6avoz6" />
          </div>

          <h1 className={`text-3xl font-bold mb-4 tracking-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2707" data-mp="2trw6wu4xgaiz">
            What's your number?
          </h1>
          <p className={`text-base leading-relaxed mb-4 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2708" data-mp="220gkp17a8pta">
            {deliveryMethod === 'whatsapp' ? "We'll send a secure verification code to your WhatsApp to set up your AI assistant." : "We'll call you with a secure verification code to set up your AI assistant."}
          </p>

          <div className={`inline-flex items-center gap-1.5 self-start mb-8 px-3 py-1.5 rounded-full border ${deliveryMethod === 'whatsapp' ? isLight ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : isLight ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-blue-500/10 border-blue-500/20 text-blue-300'}`} data-id="element-2709" data-mp="3mdpd7dpfm7ai">
            {deliveryMethod === 'whatsapp' ? <MessageCircle className="w-3.5 h-3.5" data-id="element-2710" data-mp="1rtx52w2um17k" /> : <Phone className="w-3.5 h-3.5" data-id="element-2711" data-mp="1rwt4ypy9azyn" />}
            <span className="text-xs font-semibold" data-id="element-2712" data-mp="1vuquxuzupq33">
              {deliveryMethod === 'whatsapp' ? 'Verification via WhatsApp' : 'Verification via phone call'}
            </span>
          </div>

          <div className="w-full space-y-8" data-id="element-2713" data-mp="1n1m3zpwdg9df">
            <div className="relative" data-id="element-2714" data-mp="2at1bry6x3iw2">
              <label className={`absolute -top-2.5 left-4 px-1.5 text-xs font-medium z-10 transition-colors ${isLight ? 'bg-stone-100 text-slate-500' : 'bg-black text-zinc-500'}`} data-id="element-2715" data-mp="2arzzjqe60qcr">
                Phone Number
              </label>
              <div className={`relative flex items-center gap-3 p-2 rounded-2xl border-2 transition-all duration-300 ${isLight ? 'bg-white border-slate-200 focus-within:border-slate-900 focus-within:shadow-sm' : 'bg-white/5 border-white/10 focus-within:border-white focus-within:bg-white/[0.07]'}`} data-id="element-2716" data-mp="lyu9oxej2pu3">
                <div className={`flex items-center justify-center pl-3 pr-2 gap-1.5 shrink-0 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2717" data-mp="19o6ezm6qidr4">
                  <span className="text-lg leading-none" data-id="element-2718" data-mp="10vcpyx1hk002">🇺🇸</span>
                  <span className="font-semibold tracking-wide" data-id="element-2719" data-mp="vxjh1w8ssrw3">+1</span>
                  <ChevronDown className={`w-3.5 h-3.5 opacity-50`} data-id="element-2720" data-mp="vvgvppilbmxp" />
                </div>
                <div className={`w-px h-6 ${isLight ? 'bg-slate-200' : 'bg-white/10'}`} data-id="element-2721" data-mp="1tes0f5jhkgiz" />
                <input placeholder="(555) 123-4567" type="tel" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} className={`flex-1 h-12 bg-transparent outline-none text-lg font-medium tracking-wide placeholder:font-normal ${isLight ? 'text-slate-900 placeholder:text-slate-300' : 'text-white placeholder:text-zinc-600'}`} autoFocus data-id="element-2722" data-mp="1vx3km28kekx1" />
              </div>
            </div>

            <div data-id="element-2723" data-mp="2juixn53pzuz0">
              <p className={`text-xs font-semibold uppercase tracking-[0.08em] mb-2.5 ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-2724" data-mp="2ju0tzneqaeh2">
                Send code by
              </p>
              <div className="grid grid-cols-2 gap-2.5" role="radiogroup" aria-label="Send code by" data-id="element-2725" data-mp="2tjkgr45kash7">
                {[{
                id: 'whatsapp' as const,
                title: 'WhatsApp',
                description: 'Get your code in WhatsApp',
                icon: MessageCircle,
                // Channel identity — always shown, independent of selection.
                tile: isLight ? 'bg-emerald-50 text-emerald-600' : 'bg-emerald-500/15 text-emerald-400'
              }, {
                id: 'call' as const,
                title: 'Phone call',
                description: 'Hear your code over a call',
                icon: Phone,
                tile: isLight ? 'bg-blue-50 text-blue-600' : 'bg-blue-500/15 text-blue-300'
              }].map(method => {
                const selected = deliveryMethod === method.id;
                const Icon = method.icon;
                return <button key={method.id} type="button" role="radio" onClick={() => setDeliveryMethod(method.id)} aria-checked={selected} className={`relative p-3 rounded-2xl border text-left transition-[border-color,background-color,box-shadow] duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-vexu/30 ${selected ? isLight ? 'bg-white border-vexu ring-1 ring-vexu shadow-sm' : 'bg-white/[0.06] border-vexu ring-1 ring-vexu' : isLight ? 'bg-white border-slate-200 hover:border-slate-300' : 'bg-white/5 border-white/10 hover:border-white/20'}`} data-id="element-2726" data-mp="1q728o0t8zfe3">
                      <span className={`absolute right-2.5 top-2.5 grid h-4 w-4 place-items-center rounded-full border transition-colors duration-150 ${selected ? 'border-vexu bg-vexu text-white' : isLight ? 'border-slate-300 bg-white' : 'border-white/25'}`} aria-hidden data-id="element-2727" data-mp="1tqj93g8g75x9">
                        {selected && <Check className="h-2.5 w-2.5" strokeWidth={3.5} data-id="element-2728" data-mp="3a5u504uhyz12" />}
                      </span>
                      <div className={`w-8 h-8 mb-2 rounded-xl flex items-center justify-center ${method.tile}`} data-id="element-2729" data-mp="3e4jtash2q0zh">
                        <Icon className="w-4 h-4" data-id="element-2730" data-mp="21z562yfl4hh2" />
                      </div>
                      <p className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2731" data-mp="1wyzknzzwrr7f">
                        {method.title}
                      </p>
                      <p className={`text-[10px] leading-snug mt-0.5 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2732" data-mp="1n6k49749faon">
                        {method.description}
                      </p>
                    </button>;
              })}
              </div>
            </div>

            <Button fullWidth size="lg" onClick={handleContinue} loading={isLoading} disabled={!phoneNumber} icon={<ArrowRight className="w-5 h-5" data-id="element-2734" data-mp="2yntdl0tocmc8" />} className="flex-row-reverse" data-id="element-2733" data-mp="14abx1xrospqp">
              Continue
            </Button>
          </div>
        </motion.div>
      </div>

      {/* Loading Overlay */}
      <AnimatePresence data-id="element-2735" data-mp="3b5459crs8gbp">
        {isLoading && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 z-50 flex flex-col items-center justify-center p-8" data-id="element-2736" data-mp="3b3t2nga2pem6">
            <div className={`absolute inset-0 ${isLight ? 'bg-white/95' : 'bg-[#0A0A0A]/95'} backdrop-blur-xl`} data-id="element-2737" data-mp="2s71iz5qmcxhl" />
            <div className="relative z-10 flex flex-col items-center text-center w-full max-w-[280px]" data-id="element-2738" data-mp="2x5mnvigtg5cd">
              <div className="relative w-32 h-32 mb-8" data-id="element-2739" data-mp="3272858b667tp">
                <motion.div animate={{
              scale: [1, 1.3, 1],
              opacity: [0.3, 0.1, 0.3]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="absolute inset-0 rounded-full border-2 border-vexu/30" data-id="element-2740" data-mp="cgos5gsiwot2" />
                <motion.div animate={{
              rotate: 360
            }} transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: 'linear'
            }} className="absolute inset-4 rounded-full border-2 border-transparent border-t-vexu border-r-vexu/30" data-id="element-2741" data-mp="3ogd1ijsrd8f" />
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-2742" data-mp="3g30i3g4j2y0z">
                  <motion.div animate={{
                scale: [1, 1.1, 1]
              }} transition={{
                duration: 1.5,
                repeat: Infinity
              }} data-id="element-2743" data-mp="3l4psctjxf7o0">
                    <Smartphone className="w-10 h-10 text-vexu" data-id="element-2744" data-mp="1ahw3vyp8wa84" />
                  </motion.div>
                </div>
              </div>
              <p className={`text-base font-semibold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2745" data-mp="15it68zblh6a6">
                {deliveryMethod === 'whatsapp' ? 'Sending Code via WhatsApp' : 'Preparing your verification call'}
              </p>
              <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2746" data-mp="rs67j75u3ge3">
                Preparing your verification...
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}